import React, { useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import {
  useStore,
  BuddyListScreen,
  ImWindow,
  SignOnScreen,
  SignUpScreen,
  AwayMessageScreen,
  SettingsScreen,
} from '@ogaim/app';
import { RunningMan, useWallpaper } from '@ogaim/ui';
import { normalizeScreenName } from '@ogaim/core';
import { WindowFrame, useWindowManager, type WindowState } from './Win95Window';

const BUDDY_LIST_ID = 'buddylist';
const AWAY_ID = 'away';
const SETTINGS_ID = 'settings';
const SIGNON_ID = 'signon';

/**
 * The Win95 desktop. Windows are chrome-free here — the shared screens each
 * render their own <Window>, so the desktop hosts only their *contents* and
 * supplies the frame itself. That keeps one implementation of the buddy list
 * across iOS, macOS and web.
 */
export function Desktop() {
  const screen = useStore((s) => s.screen);
  const me = useStore((s) => s.me);
  const openWindows = useStore((s) => s.openWindows);
  const conversations = useStore((s) => s.conversations);
  const closeConversation = useStore((s) => s.closeConversation);
  const focusConversation = useStore((s) => s.focusConversation);
  const signOff = useStore((s) => s.signOff);

  const wallpaper = useWallpaper();
  const signedIn = screen === 'buddylist';
  const wm = useWindowManager();
  const [startOpen, setStartOpen] = useState(false);

  // The buddy list is the desktop's anchor window; it appears on sign-on and
  // disappears on sign-off.
  useEffect(() => {
    if (signedIn) {
      wm.open({
        id: BUDDY_LIST_ID,
        title: me ? `${me.screenName}'s Buddy List` : 'Buddy List',
        x: Math.max(16, window.innerWidth - 300),
        y: 24,
        width: 260,
        height: Math.min(560, window.innerHeight - 90),
      });
    } else {
      wm.close(BUDDY_LIST_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn]);

  // Each open conversation gets a window, cascaded so they do not stack exactly.
  useEffect(() => {
    openWindows.forEach((key, i) => {
      const convo = conversations[key];
      if (!convo) return;
      wm.open({
        id: `im:${key}`,
        title: `Instant Message from ${convo.screenName}`,
        x: 60 + i * 26,
        y: 60 + i * 26,
        width: 420,
        height: 380,
      });
    });
    // Drop frames for conversations that were closed elsewhere.
    wm.windows
      .filter((w) => w.id.startsWith('im:') && !openWindows.includes(w.id.slice(3)))
      .forEach((w) => wm.close(w.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openWindows, conversations]);

  const ordered = useMemo(() => [...wm.windows].sort((a, b) => a.z - b.z), [wm.windows]);

  const renderBody = (w: WindowState) => {
    if (w.id === BUDDY_LIST_ID) {
      return (
        <BuddyListScreen
          frame={false}
          onOpenSettings={() =>
            wm.open({
              id: SETTINGS_ID,
              title: 'Settings',
              x: 150,
              y: 70,
              width: 400,
              height: 420,
            })
          }
          onEditAwayMessage={() =>
            wm.open({
              id: AWAY_ID,
              title: 'Away Message',
              x: 120,
              y: 90,
              width: 380,
              height: 460,
            })
          }
        />
      );
    }
    if (w.id === AWAY_ID) {
      return <AwayMessageScreen frame={false} onClose={() => wm.close(AWAY_ID)} />;
    }
    if (w.id === SETTINGS_ID) {
      return <SettingsScreen frame={false} onClose={() => wm.close(SETTINGS_ID)} />;
    }
    if (w.id.startsWith('im:')) {
      const key = w.id.slice(3);
      const convo = conversations[key];
      if (!convo) return null;
      return (
        <ImWindow
          screenName={convo.screenName}
          active={wm.focused === w.id}
          onClose={() => closeConversation(convo.screenName)}
          frame={false}
        />
      );
    }
    return null;
  };

  return (
    <>
      <div
        className="desktop"
        onPointerDown={() => setStartOpen(false)}
        style={
          wallpaper.uri
            ? {
                backgroundImage: `url("${wallpaper.uri}")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                // Nearest-neighbour, so the built-ins stay chunky instead of
                // being smoothed into a blur when scaled up.
                imageRendering: 'pixelated',
              }
            : undefined
        }
      >
        <DesktopIcons />

        {/* Sign-on and sign-up sit in their own frame, centred like a dialog. */}
        {!signedIn ? (
          <WindowFrame
            title={screen === 'signup' ? 'Get a Screen Name' : 'Sign On'}
            x={Math.max(12, Math.round(window.innerWidth / 2 - 170))}
            y={Math.max(12, Math.round(window.innerHeight / 2 - 250))}
            width={340}
            height={screen === 'signup' ? 560 : 470}
            icon={<RunningManGlyph />}
            onFocus={() => {}}
            onMove={() => {}}
          >
            <View style={{ flex: 1 }}>
              {screen === 'signup' ? <SignUpScreen frame={false} /> : <SignOnScreen frame={false} />}
            </View>
          </WindowFrame>
        ) : null}

        {ordered.map((w) =>
          w.minimized ? null : (
            <div key={w.id} style={{ position: 'absolute', zIndex: w.z, inset: 0, pointerEvents: 'none' }}>
              <div style={{ pointerEvents: 'auto' }}>
                <WindowFrame
                  title={w.title}
                  active={wm.focused === w.id}
                  x={w.x}
                  y={w.y}
                  width={w.width}
                  height={w.height}
                  icon={<RunningManGlyph />}
                  onFocus={() => {
                    wm.focus(w.id);
                    if (w.id.startsWith('im:')) {
                      const convo = conversations[w.id.slice(3)];
                      if (convo) focusConversation(convo.screenName);
                    }
                  }}
                  onMinimize={() => wm.minimize(w.id)}
                  onClose={
                    w.id === BUDDY_LIST_ID
                      ? undefined
                      : w.id === AWAY_ID || w.id === SETTINGS_ID
                        ? () => wm.close(w.id)
                        : () => {
                            const convo = conversations[w.id.slice(3)];
                            if (convo) closeConversation(convo.screenName);
                          }
                  }
                  onMove={(x, y) => wm.move(w.id, x, y)}
                >
                  <View style={{ flex: 1 }}>{renderBody(w)}</View>
                </WindowFrame>
              </div>
            </div>
          )
        )}
      </div>

      <Taskbar
        windows={wm.windows}
        focused={wm.focused}
        startOpen={startOpen}
        onStart={() => setStartOpen((v) => !v)}
        onPick={(id) => {
          const w = wm.windows.find((x) => x.id === id);
          if (w && !w.minimized && wm.focused === id) wm.minimize(id);
          else wm.focus(id);
        }}
      />

      {startOpen ? (
        <StartMenu
          signedIn={signedIn}
          onSignOff={() => {
            setStartOpen(false);
            signOff();
          }}
          onClose={() => setStartOpen(false)}
        />
      ) : null}
    </>
  );
}

/** The running man, sized for a caption bar. */
function RunningManGlyph() {
  return (
    <span style={{ display: 'inline-block', width: 14, height: 14, verticalAlign: 'middle' }}>
      <View style={{ width: 14, height: 14 }}>
        <RunningMan size={14} />
      </View>
    </span>
  );
}

function DesktopIcons() {
  // Decoration. They select but do nothing, exactly like a fresh install.
  const icons = [
    { label: 'My Computer', glyph: '🖥' },
    { label: 'Network Neighborhood', glyph: '🖧' },
    { label: 'Recycle Bin', glyph: '🗑' },
  ];
  return (
    <div className="desktop-icons">
      {icons.map((i) => (
        <button key={i.label} className="desktop-icon" type="button">
          <span style={{ fontSize: 28, lineHeight: 1 }} aria-hidden>
            {i.glyph}
          </span>
          <span className="label">{i.label}</span>
        </button>
      ))}
    </div>
  );
}

function Taskbar({
  windows,
  focused,
  startOpen,
  onStart,
  onPick,
}: {
  windows: WindowState[];
  focused: string | null;
  startOpen: boolean;
  onStart: () => void;
  onPick: (id: string) => void;
}) {
  const clock = useClock();
  return (
    <div className="taskbar raised" role="toolbar" aria-label="Taskbar">
      <button
        className={`start-btn raised ${startOpen ? 'open' : ''}`}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={onStart}
        aria-expanded={startOpen}
      >
        <span className="inner">
          <WindowsFlag />
          Start
        </span>
      </button>

      <div className="task-list">
        {windows.map((w) => (
          <button
            key={w.id}
            className={`task-btn raised ${focused === w.id && !w.minimized ? 'active' : ''}`}
            onClick={() => onPick(w.id)}
            title={w.title}
          >
            <span className="inner">{w.title}</span>
          </button>
        ))}
      </div>

      <div className="tray sunken">
        <span className="inner" style={{ display: 'grid', placeItems: 'center', padding: '0 6px' }}>
          <span className="clock">{clock}</span>
        </span>
      </div>
    </div>
  );
}

/** The four-pane flag, drawn with CSS so there is no image to ship. */
function WindowsFlag() {
  const pane = (bg: string): React.CSSProperties => ({ background: bg, width: 6, height: 6 });
  return (
    <span
      aria-hidden
      style={{
        display: 'grid',
        gridTemplateColumns: '6px 6px',
        gap: 1,
        transform: 'skewY(-8deg)',
      }}
    >
      <span style={pane('#ff0000')} />
      <span style={pane('#00a000')} />
      <span style={pane('#0000ff')} />
      <span style={pane('#ffc000')} />
    </span>
  );
}

function StartMenu({
  signedIn,
  onSignOff,
  onClose,
}: {
  signedIn: boolean;
  onSignOff: () => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="start-menu raised" onPointerDown={(e) => e.stopPropagation()} role="menu">
      <div className="inner">
        <div className="start-rail">
          <span>OG Instant Messenger</span>
        </div>
        <div className="start-items">
          <button className="start-item" disabled role="menuitem">
            📶 Programs
          </button>
          <button className="start-item" disabled role="menuitem">
            📄 Documents
          </button>
          <button className="start-item" disabled role="menuitem">
            ⚙️ Settings
          </button>
          <button className="start-item" disabled role="menuitem">
            🔍 Find
          </button>
          <button className="start-item" disabled role="menuitem">
            ❓ Help
          </button>
          <div className="start-sep" />
          <button className="start-item" onClick={onSignOff} disabled={!signedIn} role="menuitem">
            🔌 Sign Off…
          </button>
        </div>
      </div>
    </div>
  );
}

function useClock() {
  const [now, setNow] = useState(() => formatClock(new Date()));
  useEffect(() => {
    const id = setInterval(() => setNow(formatClock(new Date())), 10_000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function formatClock(d: Date) {
  let h = d.getHours();
  const suffix = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${String(d.getMinutes()).padStart(2, '0')} ${suffix}`;
}

export { normalizeScreenName };
